import React, { useEffect, useMemo } from 'react';
import { Container, Text } from '@chakra-ui/react';
import { Helmet } from 'react-helmet';

import { PageHeader } from 'shared-components';

import { useDispatch } from 'react-redux';
import CardItems from '../../organisms/card-items';
import { useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { ApplicationState } from '../../../store';
import { Loading } from '../../atoms/loading';
import { getConfigRecord } from '../../../store/config/actions';
import { getOfferOrIntakeMode, getUniCode } from '../../../store/selectors';
import { CoreStatusExtended, IntakeStatus } from 'types';
import { titleAppend } from '../../../utilities/head';

export type ProcessingMode = 'Offer' | 'Intake';

const IntakeSelector: React.FC = () => {
  const dispatch = useDispatch();
  const uniCode: string = useSelector(getUniCode);
  const offers = useSelector((state: ApplicationState) => state.offers?.offers || []);
  const offerOrIntake: ProcessingMode = useSelector(getOfferOrIntakeMode);

  const offersOtherThanClosed = offers.filter(offer => offer.intakeStatus !== IntakeStatus.CLOSED);

  useEffect(() => {
    dispatch(getConfigRecord(uniCode));
  }, [uniCode, dispatch]);

  const summaryText = useMemo(() => {
    const mode = offerOrIntake.toLowerCase();
    const isSelectedIntakeInactive = offersOtherThanClosed.some(
      ({ isActive, intakeStatus }) => isActive && intakeStatus === IntakeStatus.FUTURE
    );

    const isSelectedIntakeTriggered = offersOtherThanClosed.some(
      ({ isActive, status }) => isActive && status === CoreStatusExtended.NOT_TRIGGERED
    );

    if (isSelectedIntakeInactive) {
      return `You have multiple active ${mode}s. The university has not started issuing CAS statements for your chosen intake. Please await instruction.`;
    }
    if (isSelectedIntakeTriggered) {
      return `You have active offers in multiple intakes. You cannot request a CAS statement for your chosen offer yet. Please await instruction.`;
    }
    return `You have multiple active ${mode}s, please select an ${mode} to begin or resume your journey.`;
  }, [offerOrIntake, offersOtherThanClosed]);

  const { isLoading, apiCallsInProgress } = useSelector(
    (state: ApplicationState) => ({
      isLoading: state.ui.isLoading,
      apiCallsInProgress: state.data.apiCallsInProgress
    }),
    shallowEqual
  );

  const pageTitle = `Select an ${offerOrIntake}`;

  return (
    <>
      <Helmet>
        <title>
          {pageTitle} {titleAppend}
        </title>
      </Helmet>

      <Loading show={isLoading} apiCallsInProgress={apiCallsInProgress} />

      <Container as="main" my={{ base: 8, md: 16 }}>
        <PageHeader>{pageTitle}</PageHeader>

        <Text fontSize="lg" mb={8}>
          {summaryText}
        </Text>

        {offersOtherThanClosed && !!offersOtherThanClosed.length ? (
          <CardItems items={offersOtherThanClosed} mode={offerOrIntake} />
        ) : null}
      </Container>
    </>
  );
};

export default IntakeSelector;
